
import React, {useState} from "react";
import {Navigation} from "@/components/Navigation";
import {Footer} from "@/components/Footer";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {useForm} from "react-hook-form";
import {Link, useNavigate} from "react-router-dom";
import {toast} from "sonner";
import supabase from "../utils/supabaseClient.ts";
import {SEO} from "@/components/SEO.tsx";

interface LoginFormValues {
    email: string;
    password: string;
}

const Login = () => {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const form = useForm<LoginFormValues>({
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit = async (loginFormValues: LoginFormValues) => {
        setIsLoading(true);
        if (!loginFormValues.email || !loginFormValues.password) {
            return toast.error('Please check your email and password.');
        }

        try {
            const {data, error} = await supabase.auth.signInWithPassword({
                email: loginFormValues.email,
                password: loginFormValues.password,
            });

            if (error) {
                setIsLoading(false);
                return toast.error(error.message);
            }

            if(data?.session) {
                localStorage.setItem('session', JSON.stringify(data.session));
            }

            toast.success('Login successful!');
            navigate('/');
        } catch (err) {
            // console.error('Login error:', err);
            toast.error('An error occurred.');
        }

        setIsLoading(false);
    };

    return (

        <div className="min-h-screen bg-background font-sans dark:bg-background">
            <SEO
                title="Login"
                description="Log in to access your account and start voting on the latest polls"
                type="website"
                name="blankpoll"
                imageUrl="/blankpoll-banner.jpg"
            />
            <Navigation/>
            <div className="container max-w-md mx-auto pt-24 px-4">
                <div className="bg-white dark:bg-gray-800/50 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h2 className="text-2xl font-bold text-center mb-6 text-secondary dark:text-white">Welcome Back</h2>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="dark:text-gray-300">Email</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter your email"
                                                type="email"
                                                {...field}
                                                autoComplete="off"
                                                className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                            />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel className="dark:text-gray-300">Password</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter your password"
                                                type="password"
                                                {...field}
                                                className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                            />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                            <Button
                                className="w-full bg-[#E5F4EA] hover:bg-[#E5F4EA]/90 text-[#155231] dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90 dark:text-white"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Logging in..." : "Login"}
                            </Button>
                        </form>
                    </Form>
                    <div className="mt-4 text-center text-sm">
                        <Link to="/forgot-password" className="text-primary hover:text-primary/90 dark:text-gray-300 dark:hover:text-white">
                            Forgot password?
                        </Link>
                    </div>
                    <div className="mt-4 text-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Don't have an account?</span>{" "}
                        <Link to="/signup" className="text-primary hover:text-primary/90 dark:text-gray-300 dark:hover:text-white">
                            Sign up
                        </Link>
                    </div>
                </div>
            </div>
            <Footer/>
        </div>
    );
};

export default Login;
